import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  LinearProgress,
} from '@mui/material';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Layout components
import DashboardLayout from 'composites/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'composites/Navbars/DashboardNavbar';

// Auth context

// Types
import { CryptoCurrency } from '../../../packages/crypto-payments/src/models/types';
import { useUser } from 'context/user';
import PaymentTrackingModal from './components/PaymentTrackingModal';
import { CRYPTO_PAYMENTS_API_URL, PRICE_PER_TOKEN } from 'config';
import { BASE_OPTION_DEFAULTS } from '@fullcalendar/core/internal';

interface PaymentResponse {
  paymentId: string;
  systemAddress: string;
  senderAddress: string;
  currency: CryptoCurrency;
  amount: number;
  rawAmount: number;
  paymentIdentifier: string;
  expiresAt: string;
  usdEquivalent?: number;
  exchangeRate?: number;
  credits: number;
}

interface CryptoPrice {
  price: number;
  lastUpdated: Date;
  symbol: string;
  name: string;
  percentChange24h: number;
}

interface CryptoCostResponse {
  currency: string;
  price: number;
  timestamp: string;
}

function TopUp(): JSX.Element {
  const [currency, setCurrency] = useState<CryptoCurrency>('BTC');
  const [amount, setAmount] = useState<string>('');
  const [creditsAmount, setCreditsAmount] = useState<string>('');
  const [cryptoCost, setCryptoCost] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentResponse | null>(null);
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [walletAddressError, setWalletAddressError] = useState<string>('');
  const [cryptoPrices, setCryptoPrices] = useState<
    Record<CryptoCurrency, CryptoPrice | null>
  >({
    BTC: null,
    XRP: null,
    ETH: null,
  });
  const [isTrackingModalOpen, setIsTrackingModalOpen] =
    useState<boolean>(false);
  const { accessToken, user } = useUser();
  const navigate = useNavigate();
  const interval = useRef<NodeJS.Timeout | null>(null);

  // Fetch crypto prices on component mount and when currency changes
  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        const response = await fetch(
          `${CRYPTO_PAYMENTS_API_URL}/api/crypto/prices`
        );
        if (response.ok) {
          const data = await response.json();
          setCryptoPrices(data.prices);
        }
      } catch (err) {
        console.error('Failed to fetch crypto prices:', err);
      }
    };

    fetchCryptoPrices();
    // Set up a refresh interval (every 60 seconds)
    interval.current = setInterval(fetchCryptoPrices, 60000);

    return () => clearInterval(interval.current);
  }, []);

  useEffect(() => {
    if (isTrackingModalOpen) {
      clearInterval(interval.current);
    }
  }, [isTrackingModalOpen]);

  // Fetch current price for selected currency and update crypto cost
  useEffect(() => {
    const fetchCryptoPrice = async () => {
      try {
        const response = await fetch(
          `${CRYPTO_PAYMENTS_API_URL}/api/crypto/price/${currency}`
        );
        if (response.ok) {
          const data: CryptoCostResponse = await response.json();

          // If credits amount is set, calculate crypto cost
          if (creditsAmount) {
            const credits = parseFloat(creditsAmount);
            const cryptoAmount = credits / data.price;
            setCryptoCost(cryptoAmount.toFixed(8));
          }
        }
      } catch (err) {
        console.error(`Failed to fetch ${currency} price:`, err);
      }
    };

    if (currency && user) {
      fetchCryptoPrice();
    }
  }, [currency, creditsAmount, user]);

  const validateWalletAddress = () => {
    if (!walletAddress.trim()) {
      setWalletAddressError('Wallet address is required');
      return false;
    }

    // Basic validation - can be enhanced with currency-specific validation
    if (walletAddress.length < 10) {
      setWalletAddressError('Invalid wallet address format');
      return false;
    }

    setWalletAddressError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate wallet address
    if (!validateWalletAddress()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      console.log('cryptoCost', cryptoCost);
      // Use USD payment endpoint (1 credit = 1 USD)
      response = await fetch(`${CRYPTO_PAYMENTS_API_URL}/api/crypto/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: accessToken,
          currency,
          walletAddress,
          credits: creditsAmount,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment');
      }

      const paymentData = await response.json();
      setPayment(paymentData);

      // Open the payment tracking modal
      setIsTrackingModalOpen(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredits = () => {
    navigate('/credits');
  };

  const handleCurrencyChange = (newCurrency: CryptoCurrency) => {
    setCurrency(newCurrency);
    setAmount('');
    setCreditsAmount('');
    setCryptoCost('');
  };

  const handleCreditsAmountChange = (value: string) => {
    setCreditsAmount(value);

    // Calculate crypto amount based on current price
    if (value && cryptoPrices[currency]?.price) {
      const calculatedCrypto =
        parseFloat(value) / cryptoPrices[currency]!.price;
      setAmount(calculatedCrypto.toFixed(8));
    } else {
      setAmount('');
    }
  };

  const handleWalletAddressChange = (value: string) => {
    setWalletAddress(value);
    if (walletAddressError) {
      validateWalletAddress();
    }
  };

  const handlePaymentSuccess = () => {
    // Refresh user data or navigate to credits page
    setTimeout(() => {
      navigate('/credits');
    }, 5000);
  };

  const renderPaymentForm = () => (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }}></Box>
            <FormControl fullWidth>
              <InputLabel id="currency-select-label">Currency</InputLabel>
              <Select
                labelId="currency-select-label"
                id="currency-select"
                value={currency}
                label="Currency"
                onChange={(e) =>
                  handleCurrencyChange(e.target.value as CryptoCurrency)
                }
              >
                <MenuItem value="BTC">Bitcoin (BTC)</MenuItem>
                {/* <MenuItem value="XRP">Ripple (XRP)</MenuItem> */}
                {/* <MenuItem value="ETH">Ethereum (ETH)</MenuItem> */}
              </Select>
            </FormControl>
          </Grid>

          {cryptoPrices[currency] && (
            <Grid item xs={12}>
              <Alert severity="info">
                Current {currency} price: $
                {cryptoPrices[currency]!.price.toFixed(2)} USD
                <Typography variant="caption" display="block">
                  24h change:{' '}
                  {cryptoPrices[currency]!.percentChange24h.toFixed(2)}%
                </Typography>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Your Wallet Address"
              value={walletAddress}
              onChange={(e) => handleWalletAddressChange(e.target.value)}
              error={!!walletAddressError}
              helperText={walletAddressError}
              required
              placeholder={`Enter your ${currency} wallet address`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Credits"
              type="number"
              value={creditsAmount}
              onChange={(e) => handleCreditsAmountChange(e.target.value)}
              InputProps={{
                inputProps: { min: 0, step: 0.01 },
              }}
              required
              helperText={amount ? `≈ ${amount} ${currency}` : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={`${currency} Cost`}
              value={cryptoCost ? `${cryptoCost} ${currency}` : ''}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              helperText="Cost in cryptocurrency based on current price"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !creditsAmount}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Payment'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const renderPaymentDetails = () => {
    if (!payment) return null;

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please send exactly {payment.amount} {payment.currency} to the address
          below. Include the payment identifier in the transaction memo/tag if
          applicable.
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Payment Address:</Typography>
            <TextField
              fullWidth
              value={payment.senderAddress}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Payment Identifier:</Typography>
            <TextField
              fullWidth
              value={payment.paymentIdentifier}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Amount:</Typography>
            <TextField
              fullWidth
              value={`${payment.amount} ${payment.currency}`}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          {payment.usdEquivalent && payment.exchangeRate && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">Credits Equivalent:</Typography>
              <TextField
                fullWidth
                value={`${(payment.usdEquivalent / PRICE_PER_TOKEN).toFixed(
                  2
                )} Credits (Rate: $${payment.exchangeRate.toFixed(2)})`}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Your Wallet Address:</Typography>
            <TextField
              fullWidth
              value={walletAddress}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Expires At:</Typography>
            <TextField
              fullWidth
              value={new Date(payment.expiresAt).toLocaleString()}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleBackToCredits}
            >
              Back to Credits
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={3}>
                  Top Up Credits
                </MDTypography>
                <Divider sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {payment ? renderPaymentDetails() : renderPaymentForm()}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Payment Tracking Modal */}
      <PaymentTrackingModal
        open={isTrackingModalOpen}
        payment={payment}
        onClose={() => setIsTrackingModalOpen(false)}
        onSuccess={handlePaymentSuccess}
      />
    </DashboardLayout>
  );
}

export default TopUp;
